import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { ROLES, usernameToEmail, type Role } from '@agrogruda/shared';
import { sq } from '@/i18n/sq';
import { supabase, type Profile } from '@/lib/supabase';
import { clearProfileMirror, loadProfileMirror, saveProfileMirror } from '@/pwa/profileMirror';

export type AuthStatus = 'loading' | 'anonymous' | 'authenticated';

export interface AuthContextValue {
  status: AuthStatus;
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  role: Role | null;
  /** Prototype authLogin(): resolves with the profile, rejects with the verbatim error text. */
  signIn: (username: string, password: string) => Promise<Profile>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function isRole(value: string): value is Role {
  return (ROLES as readonly string[]).includes(value);
}

/** Prototype roleLabel() falls back to staff for unknown roles; the same applies to permissions. */
export function roleOf(profile: Profile | null): Role | null {
  if (!profile) return null;
  return isRole(profile.role) ? profile.role : 'staff';
}

export async function loadProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).maybeSingle();
  if (error) return null;
  return data;
}

export interface ProfileLoad {
  profile: Profile | null;
  /** The read failed (offline / server unreachable) — `profile` is the mirrored copy, if any. */
  offline: boolean;
}

/**
 * Persisted-session profile read with the offline mirror (task.md §4.14): a failed request must not sign the
 * user out — the last mirrored profile keeps the shell usable; a successful read refreshes the mirror.
 */
export async function loadProfileOrMirror(userId: string): Promise<ProfileLoad> {
  const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).maybeSingle();
  if (error) return { profile: loadProfileMirror(userId), offline: true };
  if (data?.active) saveProfileMirror(data);
  else clearProfileMirror(userId);
  return { profile: data, offline: false };
}

interface LoadedProfile {
  userId: string;
  profile: Profile;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // undefined = the persisted session has not been read yet
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [loaded, setLoaded] = useState<LoadedProfile | null>(null);

  useEffect(() => {
    let cancelled = false;
    void supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) setSession(data.session);
    });
    const { data: subscription } = supabase.auth.onAuthStateChange((_event, next) => {
      // No Supabase calls in here: awaiting inside this callback would block the auth lock.
      setSession(next);
    });
    return () => {
      cancelled = true;
      subscription.subscription.unsubscribe();
    };
  }, []);

  const userId = session?.user.id ?? null;

  useEffect(() => {
    if (!userId) {
      setLoaded(null);
      return;
    }
    let cancelled = false;
    void loadProfileOrMirror(userId).then(({ profile, offline }) => {
      if (cancelled) return;
      if (!profile?.active) {
        // Prototype: only users with active !== false may hold a session. Offline with no mirror: stay
        // 'loading' until the connection is back rather than dropping a valid session.
        if (!offline) {
          void supabase.auth.signOut();
          clearProfileMirror(userId);
        }
        setLoaded(null);
        return;
      }
      setLoaded({ userId, profile });
    });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const signIn = useCallback(async (username: string, password: string): Promise<Profile> => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: usernameToEmail(username),
      password,
    });
    if (error || !data.user) throw new Error(sq.auth.invalidCredentials);
    const profile = await loadProfile(data.user.id);
    if (!profile?.active) {
      await supabase.auth.signOut();
      throw new Error(sq.auth.invalidCredentials);
    }
    saveProfileMirror(profile);
    setLoaded({ userId: data.user.id, profile });
    setSession(data.session);
    return profile;
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    if (userId) clearProfileMirror(userId);
    await supabase.auth.signOut();
    setLoaded(null);
    setSession(null);
  }, [userId]);

  const refreshProfile = useCallback(async (): Promise<void> => {
    if (!userId) return;
    const profile = await loadProfile(userId);
    if (profile) setLoaded({ userId, profile });
  }, [userId]);

  const value = useMemo<AuthContextValue>(() => {
    const profile = loaded?.userId === userId ? loaded.profile : null;
    let status: AuthStatus;
    if (session === undefined) status = 'loading';
    else if (session === null) status = 'anonymous';
    else status = profile ? 'authenticated' : 'loading';
    return {
      status,
      session: session ?? null,
      user: session?.user ?? null,
      profile,
      role: roleOf(profile),
      signIn,
      signOut,
      refreshProfile,
    };
  }, [session, loaded, userId, signIn, signOut, refreshProfile]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
